import { IFoodItem, IMenuJSON } from "../../../../redux/food/food.types";
import {
  ICategoryGroup,
  ISubcategoryGroup,
} from "../types/menuBuilder.types";
import { parseSelectionKey } from "./selectionKey";

/**
 * Groups selected food items by category and subcategory,
 * preserving the ordering defined in the master menu data.
 *
 * Uses _selectionKey ("categoryUuid|subCategoryUuid|itemUuid") as the source
 * of truth for which category/sub an item was placed into — NOT category_list
 * or sub_category_uuid, which reflect the item's DB-level memberships and can
 * span multiple categories.
 *
 * - Excludes categories with zero selected items
 * - Excludes subcategories with zero selected items
 */
export function groupSelectedItemsByCategory(
  selectedItems: IFoodItem[],
  masterMenuData: IMenuJSON[],
): ICategoryGroup[] {
  if (!selectedItems.length || !masterMenuData.length) {
    return [];
  }

  // Build lookups keyed by the PLACEMENT category/sub from _selectionKey
  const itemsByCategoryUuid = new Map<string, IFoodItem[]>();
  const itemsBySubcategoryUuid = new Map<string, IFoodItem[]>();

  for (const item of selectedItems) {
    const key = (item as any)._selectionKey as string | undefined;

    let placedCategoryUuid: string | undefined;
    let placedSubCategoryUuid: string | undefined;

    if (key) {
      // _selectionKey is the definitive source — use it exclusively
      const parsed = parseSelectionKey(key);
      placedCategoryUuid = parsed.categoryUuid || undefined;
      placedSubCategoryUuid = parsed.subCategoryUuid || undefined;
    } else {
      // Fallback for items that haven't been stamped (should not happen in practice)
      placedCategoryUuid = item.category_list?.[0]?.category_uuid ?? undefined;
      placedSubCategoryUuid = item.sub_category_uuid ?? undefined;
    }

    if (placedCategoryUuid) {
      const existing = itemsByCategoryUuid.get(placedCategoryUuid);
      if (existing) {
        existing.push(item);
      } else {
        itemsByCategoryUuid.set(placedCategoryUuid, [item]);
      }
    }

    if (placedSubCategoryUuid) {
      // Key sub items under "subUuid@catUuid" so the same sub in different
      // categories doesn't bleed across (edge case but safe)
      const subKey = `${placedSubCategoryUuid}@${placedCategoryUuid ?? ""}`;
      const existing = itemsBySubcategoryUuid.get(subKey);
      if (existing) {
        existing.push(item);
      } else {
        itemsBySubcategoryUuid.set(subKey, [item]);
      }
    }
  }

  const result: ICategoryGroup[] = [];
  const seenCategoryIds = new Set<string>();

  for (const category of masterMenuData) {
    const categoryUuid = category.category_uuid;

    if (seenCategoryIds.has(categoryUuid)) continue;

    const categoryItems = itemsByCategoryUuid.get(categoryUuid);
    if (!categoryItems || categoryItems.length === 0) continue;

    seenCategoryIds.add(categoryUuid);

    const subcategories: ISubcategoryGroup[] = [];
    const seenSubcategoryIds = new Set<string>();

    if (category.subcategories && category.subcategories.length > 0) {
      for (const subcategory of category.subcategories) {
        const subcategoryUuid = subcategory.food_subcategory_name_uuid;
        if (!subcategoryUuid || seenSubcategoryIds.has(subcategoryUuid)) continue;

        // Look up using the scoped key "subUuid@catUuid"
        const subKey = `${subcategoryUuid}@${categoryUuid}`;
        const subcategoryItems = itemsBySubcategoryUuid.get(subKey);
        if (!subcategoryItems || subcategoryItems.length === 0) continue;

        seenSubcategoryIds.add(subcategoryUuid);

        subcategories.push({
          subcategoryId: subcategoryUuid,
          subcategoryName: subcategory.subcategory_name,
          totalItems: subcategoryItems.length,
          maxSelection: subcategory.max_selection,
          items: subcategoryItems,
        });
      }
    }

    result.push({
      categoryId: categoryUuid,
      categoryName: category.category_name,
      totalItems: categoryItems.length,
      totalSubcategories: subcategories.length,
      maxSelection: category.max_selection,
      subcategories,
    });
  }

  return result;
}
